import {
	AGILOX_API_ENDPOINT,
	App,
	APP_NAME,
	AppNavMenuPointsService,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	PLAUSIBLE_DOMAIN,
	PlausibleService,
	provideDatepickerTranslationsWithNgxTranslate,
	provideFavicon,
	provideSelectTranslationsWithNgxTranslate,
	provideTableSort,
	SummaryOfTheYearService,
	ThemeService,
	TranslationsLoader,
	USER_FORMAT,
} from '@agilox/common';
import { ToastService, UserNotificationsService } from '@agilox/components';
import { createErrorHandlerInterceptor } from '@agilox/interceptors';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withHashLocation } from '@angular/router';
import {
	AUTH_CONFIG,
	AuthModule,
	AuthService,
	MSALAUTHConfigFactory,
	userInitializationFactory,
} from '@auth';
import { environment } from '@manage/env/environment';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { provideUserContext, UserStoreService } from '@user-store';
import { appRoutes } from './app.routes';
import { desktopMenuPoints } from './general/shared/models/consts/desktop-menupoints';
import { mobileMenuPoints } from './general/shared/models/consts/mobile-menupoints';

export const appConfig: ApplicationConfig = {
	providers: [
		provideFavicon(),
		importProvidersFrom(AuthModule),
		UserStoreService,
		...provideUserContext(),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(appRoutes, withHashLocation()),
		provideHttpClient(
			withXhr(),
			withInterceptors([
				createErrorHandlerInterceptor({ app: App.manage, environment, onError: 'propagate' }),
				headersInterceptor,
			]),
			withInterceptorsFromDi()
		),
		provideAnimations(),
		provideSelectTranslationsWithNgxTranslate(),
		provideTableSort(),
		provideDatepickerTranslationsWithNgxTranslate(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		provideAppInitializer(() => {
			const initializerFn = () => {
				const traceService = inject(Sentry.TraceService);
				return () => {};
			};
			return Promise.resolve(initializerFn());
		}),
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useClass: TranslationsLoader,
				deps: [HttpClient],
			},
		}),
		{
			provide: MENU_POINTS,
			useValue: {
				desktop: desktopMenuPoints,
				mobile: mobileMenuPoints,
			},
		},
		MenuPointService,
		provideAppInitializer(() => {
			const authService = inject(AuthService);
			const userStore = inject(UserStoreService);
			return userInitializationFactory(authService.msalInstance, userStore)();
		}),
		AppNavMenuPointsService,
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		provideAppInitializer(() => {
			inject(PlausibleService);
		}),
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		{
			provide: APP_NAME,
			useValue: App.manage,
		},
		{
			provide: PLAUSIBLE_DOMAIN,
			useValue: 'manage.agilox.net',
		},
		PlausibleService,
		{
			provide: AGILOX_API_ENDPOINT,
			useValue: environment.server,
		},
		{
			provide: UserNotificationsService,
			deps: [AGILOX_API_ENDPOINT],
		},
		localeProvider,
		DatePipe,
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		ToastService,
		SummaryOfTheYearService,
	],
};
